import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {IYii} from '../../../../globals';
import {CommonModule} from '@angular/common';
import {NgSelectComponent} from '@ng-select/ng-select';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {CalculationFormulaAllowedSymbols, CalculationService} from '../../services/CalculationService';
import {Subscription} from 'rxjs';
import {FormService} from '../../services/FormService';
import {FieldService} from '../../services/FieldService';
import {OperationSamples} from '../../constants/operation-samples';
import {AvailableCalculationOperations} from '../../constants/available-calculation-operations';
import {Checkbox} from 'primeng/checkbox';
import {Message} from 'primeng/message';
import {Select} from 'primeng/select';
import {Textarea} from 'primeng/textarea';

declare var Yii: IYii;

const OPERATION_TYPE_DIVISION = 'division';
const OPERATION_TYPE_DEDUCTION = 'deduction';

@Component({
  selector: 'ng-field-calculated-fields',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    NgSelectComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    Checkbox,
    Message,
    Select,
    Textarea
  ],
  template: `
    <div class="form-row">
      <label style="margin-top:12px">
        {{ Yii.t('app', 'Selected Fields') }}
        <span class="text-danger">*</span>
      </label>
      <div>
        <ng-select
          [items]="selectableFields"
          [multiple]="true"
          [hideSelected]="true"
          bindLabel="tag"
          bindValue="key"
          placeholder="Select..."
          appendTo="body"
          [(ngModel)]="field.selected_fields"
          (change)="checkChangesInCalculatedFields()">
        </ng-select>
        <div style="margin-top: 1rem">
          <p-message severity="success">
            {{ Yii.t('app', 'Available Field Types: scale, dropdown, radio and textfield (number only)') }}
          </p-message>
        </div>
      </div>
    </div>
    <div class="form-row flex-align-center">
      <label>{{ Yii.t('app', 'Operation Type') }}
        <span class="text-danger">*</span>
      </label>
      <div>
        <p-select
          [(ngModel)]="field.operation"
          (change)="getExpression()"
          [options]="availableOperations"
          optionLabel="name"
          optionValue="value"
          appendTo="body"
        />
      </div>
    </div>
    <!--Percent Value-->
    <div class="form-row flex-align-center">
      <label>{{ Yii.t('app', 'Result type') }}
        <span class="text-danger">*</span>
      </label>
      <div>
        <p-select
          [(ngModel)]="field.percent_sign"
          [options]="[{name: 'Number', value: ''}, {name: 'Percent (%)', value: '%'}]"
          optionLabel="name"
          optionValue="value"
          appendTo="body"
        />
      </div>
    </div>
    <!--Percent Value-->
    <!--Sorting -->
    @if ((field.operation === 'deduction' || field.operation === 'division') && field.order?.length) {
      <div class="form-row">
        <label>{{ Yii.t('app', 'Order') }}</label>
        <div>
          <ul cdkDropList
              style="width:480px;margin: 0;padding:4px; gap: 4px; display: flex;list-style: none; border: solid 1px silver;border-radius: 4px;"
              id="sort-area"
              cdkDropListOrientation="horizontal"
              (cdkDropListDropped)="drop($event)">
            @for (item of field.order; track item) {
              <li
                cdkDrag
                [id]="item"
                style="cursor: move;">
                <div
                  cdkDragHandle
                  style="margin: 0;padding:4px 8px;border:dotted 1px silver;border-radius: 4px; background-color: #EEE">
                  {{ fsvc.calculation.GetFieldName(Number(item)) }}
                </div>
              </li>
            }
          </ul>
          <div style="margin: 0">
            {{ Yii.t('app', 'Formula:') }}&nbsp;&nbsp;
            <span class="label label-success" style="font-size: 14px;">
            <bdo dir="auto" [innerHTML]="formula"></bdo>
          </span>
          </div>
        </div>
      </div>
    }
    <!--Subtotal-->
    <div class="form-row">
      <label>{{ Yii.t('app', 'Hide this field (subtotal)') }}</label>
      <div>
        <p-checkbox [(ngModel)]="field.use_as_subtotal" [binary]="true"/>
      </div>
    </div>
    <!--Subtotal-->
    <!--Open Formula-->
    @if (field.operation === 'open_formula') {
      <div class="form-row">
        <label>{{ Yii.t('app', 'Open Formula') }}
          <span class="text-danger">*</span>
        </label>
        <div>
        <textarea
          pTextarea
          style="width: 100%"
          (focus)="csvc.setAllowedSymbols(fsvc.form, field)"
          (blur)="csvc.validateOpenFormula(fsvc.form, field)"
          [(ngModel)]="field.open_formula_formula" rows="5"></textarea>
          @if (field.showError) {
            <div style="color: red;">{{ field.showError }}</div>
          }
          @if (field.allowedPlaceholders) {
            <table style="width: 100%;margin: 1rem 0">
              <caption style="text-align: left">
                <b>{{ Yii.t('app', 'Allowed placeholders (to describe fields). Fill "Selected Fields" and focus "textarea" to see actual titles:') }}</b>
              </caption>
              <thead>
              <tr>
                <th style="text-align: left">Id</th>
                <th style="text-align: left">Title</th>
              </tr>
              </thead>
              <tbody>
                @for (symbol of field.allowedPlaceholders; track symbol.key) {
                  @if (symbol.key || symbol.key === 0) {
                    <tr>
                      <td style="width: 150px">"{{ symbol.key }}"</td>
                      <td>"{{ symbol.title }}"</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          }
          @if (allowedSymbols) {
            <div class="label-info" style="margin-top:5px; font-size: 14px;color: #ffffff;cursor: auto;">
              <b>{{ Yii.t('app', 'Allowed symbols: ') }}</b>
              <span>A-z, ", ' ,</span>
              @for (symbol of allowedSymbols; track $index) {
                <span>"{{ symbol }}",</span>
              }
            </div>
          }
          @if (operationSamples) {
            <table style="width: 100%">
              <caption style="text-align: left">
                <b>{{ Yii.t('app', 'Operations samples:') }}</b>
              </caption>
              <tbody>
                @for (operation of operationSamples; track $index) {
                  <tr>
                    <td style="width: 150px">{{ operation.operation }}</td>
                    <td>{{ operation.description }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      </div>
    }
    <!--Open Formula-->
  `
})
export class FieldCalculatedFields implements OnInit, OnDestroy {
  Yii = Yii;
  availableOperations = AvailableCalculationOperations;
  operationSamples = OperationSamples;
  allowedSymbols = CalculationFormulaAllowedSymbols;

  private calcUpdateSub: Subscription | undefined;
  public formula = '';

  // private readonly fieldAddedSub: ()=>void

  @Input() field: any;

  constructor(
    public csvc: CalculationService,
    public fsvc: FormService,
    public fieldService: FieldService
  ) {
    this.fieldService.OnFieldAddOrRemoved.subscribe(() => {
      this.updateSelectableFields();
      this.getExpression();
    })
  }

  selectableFields: any[] = [];

  updateSelectableFields() {
    this.selectableFields = this.fsvc.calculation.Fields?.filter((f: any) => f.key !== this.field.field_id) ?? [];
  }

  ngOnInit(): void {
    this.updateSelectableFields();
    this.getExpression();
    this.checkChangesInCalculatedFields();
  }

  ngOnDestroy(): void {
    this.calcUpdateSub?.unsubscribe();
    // this.fieldAddedSub();
  }

  getExpression(): void {
    if (this.field.operation !== 'open_formula' && this.field.open_formula_formula) {
      this.field.open_formula_formula = '';
      this.field.allowedPlaceholders = [];
      this.field.showError = '';
    }

    if ((this.field.operation !== OPERATION_TYPE_DIVISION && this.field.operation !== OPERATION_TYPE_DEDUCTION)
      || !this.field.order?.length || !this.field.selected_fields?.length
      || this.field.selected_fields.length !== this.field.order.length
    ) {
      this.formula = Yii.t('app', 'Expression is not valid');
      return;
    }

    const sign = this.field.operation === 'deduction' ? ' - ' : ' / ';

    const formulaParts: string[] = this.field.order.map((fieldId: any) => {
      const fieldObj = this.fsvc.form.form_fields.find((f: any) => f && String(fieldId) === String(f.field_id));
      if (fieldObj) {
        const title = (fieldObj.field_title?.trim() === '') ?
          `Field${parseInt(fieldObj.field_id, 10) + 1}` : fieldObj.field_title;
        return `<bdo dir="auto">${title}</bdo>`;
      }
      return '';
    }).filter(Boolean);

    this.formula = formulaParts.join(sign);
  }

  checkChangesInSelectedFields(): void {
    const selectedIds = this.field.selected_fields?.map(String) ?? [];
    if (this.field.order) {
      // Filter current order to keep only selected fields, preserving order
      const newOrder = this.field.order
        .map(String)
        .filter((id: string) => selectedIds.includes(id));

      // Add newly selected fields to the end of the order
      selectedIds.forEach((id: string) => {
        if (!newOrder.includes(id)) {
          newOrder.push(id);
        }
      });
      this.field.order = newOrder.map(Number);
    } else {
      this.field.order = selectedIds.map(Number);
    }
  }

  checkChangesInCalculatedFields(): void {
    this.checkChangesInSelectedFields();
    this.getExpression();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.field.order, event.previousIndex, event.currentIndex);
    this.getExpression();
  }

  protected readonly Number = Number;
}
